import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkSimple, Trash, Copy, ClockCounterClockwise, UsersThree, GitDiff, WhatsappLogo } from "@phosphor-icons/react";
import { ShareCardDialog } from "@/components/ShareCardDialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { TeamPanel } from "@/components/TeamPanel";
import { VersionDiff } from "@/components/VersionDiff";
import { getLibraryApi, deleteLibraryApi, rollbackApi, shareToTeamApi, myTeamApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const chip = "flex items-center gap-1 rounded-full bg-black/5 px-2.5 py-1 text-[11px] font-medium text-ink hover:bg-black/10";

function LibraryItem({ it, team, onSelect, onDiff, onRemove, onShare, onCard }) {
  const active = it.versions[it.active_version] || it.versions[0];
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-4 shadow-sm" data-testid="library-item">
      <div className="mb-2 flex items-center justify-between gap-2 text-[11px] text-mist">
        <span className="truncate">{[it.target_tool, active?.model, !it.is_mine && it.owner_name ? `by ${it.owner_name}` : ""].filter(Boolean).join(" · ")}</span>
        {active?.quality_score > 0 && <span className="shrink-0 rounded-full bg-brand-soft px-2 py-0.5 font-semibold text-brand">{active.quality_score}</span>}
      </div>
      {it.pack_id && <p className="mb-1 text-sm font-semibold text-ink" data-testid="library-item-title">{it.title}</p>}
      <p onClick={() => onSelect(it)} data-testid="library-item-open" className="mb-3 line-clamp-3 cursor-pointer font-mono text-xs leading-relaxed text-ink hover:text-brand">{active?.prompt}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <button data-testid="library-copy-button" onClick={async () => { await navigator.clipboard.writeText(active?.prompt || ""); toast.success("Prompt copied"); }} className={chip}><Copy size={12} /> Copy</button>
        {it.pack_id && <button data-testid="library-share-card-button" onClick={() => onCard({ title: it.title, prompt: active?.prompt || "", category: "" })} className={chip}><WhatsappLogo size={12} weight="fill" /> Share card</button>}
        {it.versions.length > 1 && (
          <button data-testid="versions-toggle" onClick={() => onDiff(it)} className={chip}>
            <GitDiff size={12} /> {it.versions.length} versions · compare
          </button>
        )}
        {it.is_mine && team && (
          <label className="flex items-center gap-1.5 text-[11px] text-mist">
            <Switch data-testid="share-team-switch" checked={it.shared} onCheckedChange={(v) => onShare(it, v)} className="scale-75" /> Team
          </label>
        )}
        {it.is_mine && <button data-testid="library-delete-button" onClick={() => onRemove(it.id)} className="ml-auto text-mist hover:text-[#D70015]"><Trash size={14} /></button>}
      </div>
    </div>
  );
}

export function LibraryDrawer({ open, onOpenChange, onSelect, refreshKey }) {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState(null);
  const [tab, setTab] = useState("mine");
  const [loading, setLoading] = useState(false);
  const [diffItem, setDiffItem] = useState(null);
  const [cardItem, setCardItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [lib, t] = await Promise.all([getLibraryApi(), myTeamApi()]);
      setItems(lib);
      setTeam(t);
    } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open, refreshKey]); // eslint-disable-line

  const onTeamChange = (t) => { setTeam(t); refresh(); load(); };
  const remove = async (id) => { await deleteLibraryApi(id); toast.success("Removed from library"); load(); };
  const share = async (it, v) => { await shareToTeamApi(it.id, v); toast.success(v ? "Shared with your team" : "Made private"); load(); };
  const rollback = async (idx) => {
    const updated = await rollbackApi(diffItem.id, idx);
    toast.success(`Rolled back to version ${idx + 1}`);
    setItems((p) => p.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setDiffItem(null);
  };

  const shown = tab === "mine" ? items.filter((i) => i.is_mine) : items.filter((i) => i.shared);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="library-drawer" className="w-full border-l border-black/5 bg-[#F5F5F7] sm:max-w-md">
        <SheetHeader className="mb-4 text-left">
          <SheetTitle className="flex items-center gap-2 font-display text-2xl font-semibold text-ink"><BookmarkSimple size={22} weight="fill" className="text-brand" /> Library</SheetTitle>
          <SheetDescription className="text-xs text-mist">Saved prompts, version history and your team's shared prompts.</SheetDescription>
        </SheetHeader>
        <div className="mb-4 inline-flex rounded-full bg-black/5 p-1">
          {[["mine", "My prompts", BookmarkSimple], ["team", "Team", UsersThree]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`library-tab-${id}`} onClick={() => setTab(id)} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${tab === id ? "bg-white text-ink shadow-sm" : "text-mist"}`}>
              <Icon size={13} weight="fill" /> {label}
            </button>
          ))}
        </div>
        <div className="flex h-[calc(100vh-12rem)] flex-col gap-3 overflow-y-auto pb-6 pr-1">
          {tab === "team" && <TeamPanel team={team} onChange={onTeamChange} />}
          {loading ? (
            <p className="mt-8 text-center text-sm text-mist">Loading…</p>
          ) : shown.length === 0 ? (
            <div className="mt-10 flex flex-col items-center text-center" data-testid="library-empty">
              <ClockCounterClockwise size={34} className="text-black/20" />
              <p className="mt-3 text-sm text-mist">{tab === "mine" ? "No saved prompts yet" : team ? "Nothing shared yet — flip the Team switch on any prompt" : "Join or create a team to share prompts"}</p>
            </div>
          ) : (
            shown.map((it) => (
              <LibraryItem key={it.id} it={it} team={team} onSelect={(x) => { onSelect?.(x); onOpenChange(false); }} onDiff={setDiffItem} onRemove={remove} onShare={share} onCard={setCardItem} />
            ))
          )}
        </div>
        <ShareCardDialog item={cardItem} open={!!cardItem} onOpenChange={(v) => !v && setCardItem(null)} />
        <VersionDiff item={diffItem} open={!!diffItem} onOpenChange={(v) => !v && setDiffItem(null)} onRollback={rollback} />
      </SheetContent>
    </Sheet>
  );
}
